import { API } from '../api/API';
import useAuthStore from '../store/authStore';

/**
 * @typedef {Object} Prescription
 * @property {number} id - ID auto-généré de la prescription
 * @property {string} numero - Numéro unique de la prescription
 * @property {number} assure_id - ID de l'assuré
 * @property {number} agent_clinique_id - ID de l'agent clinique
 * @property {number} agent_labo_id - ID de l'agent laboratoire
 * @property {string} type - Type de prescription (pharma/labo)
 * @property {string} statut - Statut de la prescription
 * @property {string} matricule_assure - Matricule de l'assuré
 * @property {number} consultation_id - ID de la consultation
 * @property {string} titre - Titre de la prescription
 * @property {string} description - Description de la prescription
 * @property {Date} date_approtion_medecin_conseille - Date d'approbation
 * @property {Date} date_prescription_agent_assurance - Date prescription assurance
 * @property {Date} date_aprescription - Date de la prescription
 * @property {boolean} status_medecin_conseille - Statut médecin conseiller
 * @property {boolean} status_agent_assurance - Statut agent assurance
 * @property {string} image - URL de l'image
 * @property {boolean} is_satisfy - Indicateur de satisfaction
 * @property {Date} satisfy_date - Date de satisfaction
 * @property {string} comment_agent_pharma - Commentaire pharmacie
 * @property {string} comment_agent_labo - Commentaire laboratoire
 */

const prescriptionService = {
  getAllPrescriptions: async (page = 1, limit = 10, filters = {}) => {
    try {
      const store = useAuthStore.getState();
      const params = {
        page,
        limit,
        ...filters,
        structure_id: store.user?.structure_id
      };
      
      const response = await API.get('/prescriptions', { params });
      return {
        success: true,
        data: response.data.data,
        pagination: response.data.pagination
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des prescriptions:', error);
      throw {
        success: false,
        message: error.response?.data?.error || 'Erreur lors de la récupération des prescriptions',
        details: error.response?.data?.details
      };
    }
  },

  createPrescription: async (prescriptionData) => {
    try {
      const store = useAuthStore.getState();
      const data = {
        ...prescriptionData,
        structure_id: store.user?.structure_id,
        agent_clinique_id: store.user?.id,
        clinique_id: store.user?.partie_id,
        statut: 'en_attente',
        date_aprescription: new Date().toISOString(),
      };

      const response = await API.post('/prescriptions', data);
      return {
        success: true,
        data: response.data,
        message: 'Prescription créée avec succès'
      };
    } catch (error) {
      throw {
        success: false,
        message: error.response?.data?.error || 'Erreur lors de la création de la prescription',
        details: error.response?.data?.details
      };
    }
  },

  createPrescriptionWithActes: async ({ prescription, actes }) => {
    try {
      const store = useAuthStore.getState();
      const data = {
        prescription: {
          ...prescription,
          structure_id: store.user?.structure_id,
          agent_clinique_id: store.user?.id,
          clinique_id: store.user?.partie_id,
          statut: 'en_attente',
          date_aprescription: new Date().toISOString(),
        },
        actes: actes.map(acte => ({
          acte_medical_id: acte.acte_medical_id,
          quantite: acte.quantite,
          prix_unitaire: acte.prix_unitaire,
          posologie: acte.posologie || null,
          duree_traitement: acte.duree_traitement || null
        }))
      };

      const response = await API.post('/prescriptions/with-actes', data);
      return {
        success: true,
        data: response.data,
        message: 'Prescription avec actes créée avec succès'
      };
    } catch (error) {
      throw {
        success: false,
        message: error.response?.data?.message || 'Erreur lors de la création de la prescription avec actes',
        details: error.response?.data?.details
      };
    }
  },

  getPrescriptionById: async (id) => {
    try {
      const response = await API.get(`/prescriptions/${id}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      throw {
        success: false,
        message: error.response?.data?.error || 'Erreur lors de la récupération de la prescription',
        details: error.response?.data?.details
      };
    }
  },

  updatePrescription: async (id, updateData) => {
    try {
      const response = await API.put(`/prescriptions/${id}`, updateData);
      return {
        success: true,
        data: response.data,
        message: 'Prescription mise à jour avec succès'
      };
    } catch (error) {
      throw {
        success: false,
        message: error.response?.data?.error || 'Erreur lors de la mise à jour de la prescription',
        details: error.response?.data?.details
      };
    }
  },

  updatePrescriptionStatus: async (id, status, comment) => {
    try {
      const response = await API.put(`/prescriptions/${id}`, {
        statut: status,
        ...(comment && { [`comment_${useAuthStore.getState().user?.role}`]: comment })
      });
      return {
        success: true,
        data: response.data,
        message: 'Statut de la prescription mis à jour avec succès'
      };
    } catch (error) {
      throw {
        success: false,
        message: error.response?.data?.error || 'Erreur lors de la mise à jour du statut',
        details: error.response?.data?.details
      };
    }
  },

  deletePrescription: async (id) => {
    try {
      await API.delete(`/prescriptions/${id}`);
      return {
        success: true,
        message: 'Prescription supprimée avec succès'
      };
    } catch (error) {
      throw {
        success: false,
        message: error.response?.data?.error || 'Erreur lors de la suppression de la prescription',
        details: error.response?.data?.details
      };
    }
  },

  getPrescriptionDetails: async (id) => {
    try {
      const response = await API.get(`/prescriptions/detail/${id}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      throw {
        success: false,
        message: error.response?.data?.error || 'Erreur lors de la récupération des détails de la prescription',
        details: error.response?.data?.details
      };
    }
  },

  getAllPrescriptionDetails: async (filters = {}) => {
    try {
      const response = await API.get('/prescriptions/details', { params: filters });
      return {
        success: true,
        data: response.data.data,
        pagination: response.data.pagination
      };
    } catch (error) {
      throw {
        success: false,
        message: error.response?.data?.error || 'Erreur lors de la récupération des prescriptions détaillées',
        details: error.response?.data?.details
      };
    }
  }
};

export default prescriptionService;
